import { Injectable, inject } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { firstValueFrom, of } from "rxjs";
import { catchError } from "rxjs/operators";
import { environment } from "@env/environment";
import {
  Auth,
  signInWithEmailAndPassword,
  signInWithPopup,
  sendPasswordResetEmail,
  OAuthProvider,
  UserCredential,
} from "@angular/fire/auth";
import {
  AuthResponse,
  TokenValidationRequest,
  TokenValidationResponse,
} from "../models/auth.model";

@Injectable({ providedIn: "root" })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(Auth);
  private readonly baseUrl = `${environment.API_AUTH}/api/v1/auth`;

  async loginWithEmail(email: string, password: string): Promise<AuthResponse> {
    try {
      const userCredential = await signInWithEmailAndPassword(
        this.auth,
        email,
        password,
      );

      const firebaseToken = await userCredential.user.getIdToken();

      const response = await firstValueFrom(
        this.http.post<AuthResponse>(
          `${this.baseUrl}/session/login`,
          {
            firebaseToken,
            productCode: "HORUS",
          },
          {
            withCredentials: true,
            headers: {
              "x-requested-by": "horus-web",
            },
          },
        ),
      );

      return {
        ...response,
        firebaseToken: response.firebaseToken || firebaseToken,
      };
    } catch (error: any) {
      throw new Error(
        error.code === "auth/wrong-password" ||
        error.code === "auth/user-not-found"
          ? "Credenciales inválidas"
          : "Error al iniciar sesión",
      );
    }
  }

  async loginWithMicrosoft(): Promise<AuthResponse> {
    try {
      const provider = new OAuthProvider("microsoft.com");
      provider.setCustomParameters({
        prompt: (environment as any).firebaseProvider.prompt,
        tenant: (environment as any).firebaseProvider.tenant,
      });

      const userCredential: UserCredential = await signInWithPopup(
        this.auth,
        provider,
      );
      const firebaseToken = await userCredential.user.getIdToken();

      const response = await firstValueFrom(
        this.http.post<AuthResponse>(
          `${this.baseUrl}/session/login`,
          {
            firebaseToken,
            productCode: "HORUS",
          },
          {
            withCredentials: true,
            headers: {
              "x-requested-by": "horus-web",
            },
          },
        ),
      );

      return {
        ...response,
        firebaseToken: response.firebaseToken || firebaseToken,
      };
    } catch (error: any) {
      if (error.message?.includes("INTERNAL ASSERTION FAILED")) {
        const internalError = new Error(
          "Error interno de autenticación",
        ) as any;
        internalError.code = "auth/internal-error";
        throw internalError;
      }

      if (error.code === "auth/popup-closed-by-user") {
        const popupError = new Error("Inicio de sesión cancelado") as any;
        popupError.code = "auth/popup-closed-by-user";
        throw popupError;
      }

      if (error.code === "auth/popup-blocked") {
        const popupBlockedError = new Error("Popup bloqueado") as any;
        popupBlockedError.code = "auth/popup-blocked";
        throw popupBlockedError;
      }

      throw error;
    }
  }

  async logout(): Promise<void> {
    // First sign out from Firebase
    await this.auth.signOut();

    // Then clear server session
    // Using pipe with catchError to handle empty response gracefully
    await firstValueFrom(
      this.http
        .post<void>(
          `${this.baseUrl}/logout`,
          {},
          {
            withCredentials: true,
            headers: {
              "x-requested-by": "horus-web",
            },
            responseType: "text" as "json", // Tell Angular to expect text, not JSON
          },
        )
        .pipe(
          // If the server returns empty 200 response, that's fine
          catchError(() => {
            // Logout was successful even if response is empty
            return of(void 0);
          }),
        ),
    );
  }

  async sendPasswordResetEmail(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(this.auth, email);
    } catch (error: any) {
      throw new Error(
        error.code === "auth/user-not-found"
          ? "No existe un usuario con este correo"
          : "Error al enviar el correo de restablecimiento",
      );
    }
  }

  async validateToken(
    request: TokenValidationRequest,
  ): Promise<TokenValidationResponse> {
    return firstValueFrom(
      this.http.post<TokenValidationResponse>(
        `${this.baseUrl}/validate`,
        request,
        {
          headers: {
            "x-requested-by": "horus-web",
          },
        },
      ),
    );
  }
}
